import { memo } from "react";
import { useTranslation } from "react-i18next";

import { ParameterCard, Reveal, SdgBadge, SectionHeading, Seo, SourceNote } from "@/components";
import { Container } from "@/layouts";
import { SDGS, SOURCE_DOCUMENTS, WATER_PARAMETERS } from "@/data";
import { ROUTES, normalizeLanguage } from "@/navigations";
import { breadcrumbJsonLd } from "@/utils/seo";

import shared from "@/styles/pages.module.css";
import styles from "./styles.module.css";

const SciencePage = memo(() => {
  const { t } = useTranslation("SciencePage");
  const { t: tSeo } = useTranslation("Seo");
  const { t: tc, i18n } = useTranslation("components");
  const lang = normalizeLanguage(i18n.language);
  const points = t("problem.points", { returnObjects: true }) as string[];

  return (
    <>
      <Seo
        title={tSeo("science.title")}
        description={tSeo("science.description")}
        path={ROUTES.SCIENCE}
        jsonLd={breadcrumbJsonLd(
          [
            { name: tc("nav.home"), path: ROUTES.HOME },
            { name: tc("nav.science"), path: ROUTES.SCIENCE },
          ],
          lang,
        )}
      />

      <section className={shared.pageHeader}>
        <Container>
          <Reveal className={shared.pageHeaderInner}>
            <span className="eyebrow" data-reveal>
              {t("hero.eyebrow")}
            </span>
            <h1 className={shared.pageTitle} data-reveal>
              {t("hero.title")}
            </h1>
            <p className={shared.lede} data-reveal>
              {t("hero.lead")}
            </p>
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.sunken}`}>
        <Container>
          <Reveal className={styles.focusGrid}>
            <div className={styles.focusText}>
              <SectionHeading title={t("problem.title")} />
              <p className={styles.focusBody} data-reveal>
                {t("problem.body")}
              </p>
            </div>
            <div className={styles.questions} data-reveal>
              <ol className={styles.questionList}>
                {points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ol>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.fieldbook}`}>
        <Container>
          <Reveal className={shared.asymmetric}>
            <div>
              <SectionHeading title={t("campus.title")} lead={t("campus.body")} />
              <p className={`${shared.note} ${styles.campusNote}`}>{t("campus.note")}</p>
            </div>
            <SourceNote label="ISMP · SEEDS">
              {t("campus.body")}
            </SourceNote>
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <SectionHeading eyebrow={t("parameters.eyebrow")} title={t("parameters.title")} />
          <Reveal className={`${shared.grid3} ${shared.stackGap}`}>
            {WATER_PARAMETERS.map((p) => (
              <ParameterCard
                key={p.id}
                icon={p.icon}
                name={t(`parameters.items.${p.id}.name`)}
                description={t(`parameters.items.${p.id}.description`)}
              />
            ))}
          </Reveal>
          <Reveal>
            <p className={`${shared.note} ${styles.paramNote}`} data-reveal>
              {t("parameters.note")}
            </p>
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.atlas}`}>
        <Container>
          <Reveal className={shared.grid2}>
            <article className={styles.dimCard} data-reveal>
              <h3 className={styles.dimTitle}>{t("spatial.title")}</h3>
              <p className={styles.dimBody}>{t("spatial.body")}</p>
            </article>
            <article className={styles.dimCard} data-reveal>
              <h3 className={styles.dimTitle}>{t("viz.title")}</h3>
              <p className={styles.dimBody}>{t("viz.body")}</p>
            </article>
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <SectionHeading title={t("sdgs.title")} lead={t("sdgs.lead")} />
          <Reveal className={`${styles.sdgGrid} ${shared.stackGap}`}>
            {SDGS.map((sdg) => (
              <SdgBadge key={sdg.id} number={sdg.number} label={t(`sdgs.items.${sdg.id}`)} />
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.sunken}`}>
        <Container>
          <SectionHeading title={t("sources.title")} lead={t("sources.lead")} />
          <Reveal className={`${styles.sourceList} ${shared.stackGap}`}>
            {SOURCE_DOCUMENTS.map((doc) => (
              <div key={doc.id} className={styles.sourceItem} data-reveal>
                <span className={styles.sourceYear}>{doc.year}</span>
                <span>{tc(`sourceDocuments.${doc.id}`)}</span>
              </div>
            ))}
          </Reveal>
        </Container>
      </section>
    </>
  );
});

SciencePage.displayName = "SciencePage";
export default SciencePage;
